import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { formatViews } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";

interface MovieCardProps {
  item: MediaItem;
  rank?: number;
}

export default function MovieCard({ item, rank }: MovieCardProps) {
  const { isFavorite, toggle } = useFavorites();
  const favorited = isFavorite(item.id);

  return (
    <div className="group relative flex flex-col">
      <Link
        to={`/detail/${item.id}`}
        className="relative block w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 cursor-pointer"
      >
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title} 水母影视 在线观看`}
          className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent opacity-90"></div>

        <span className="absolute top-2 left-2 text-[11px] px-2 py-0.5 rounded-md bg-primary-600 text-background-50 font-medium">
          {item.type}
        </span>
        {typeof rank === "number" ? (
          <span
            className={`absolute top-2 right-2 w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-background-50 ${
              rank <= 3 ? "bg-accent-500" : "bg-foreground-950/70"
            }`}
          >
            {rank}
          </span>
        ) : null}

        <span className="absolute bottom-2 left-2 flex items-center gap-1 text-xs text-background-50 font-medium">
          <i className="ri-star-fill text-accent-500"></i>
          {item.rating.toFixed(1)}
        </span>
        <span className="absolute bottom-2 right-2 flex items-center gap-1 text-[11px] text-background-50/90">
          <i className="ri-fire-line"></i>
          {formatViews(item.views)}
        </span>
      </Link>

      <button
        type="button"
        aria-label={favorited ? "取消收藏" : "收藏"}
        onClick={() => toggle(item.id)}
        className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
          typeof rank === "number" ? "hidden" : "flex"
        } ${
          favorited
            ? "bg-accent-500 text-background-50"
            : "bg-foreground-950/50 text-background-50 hover:bg-foreground-950/70"
        }`}
      >
        <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
      </button>

      <Link to={`/detail/${item.id}`} className="mt-2.5 cursor-pointer">
        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 truncate group-hover:text-accent-600 transition-colors">
          {item.title}
        </h3>
      </Link>
      <div className="mt-1 flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
        <span>{item.year}</span>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span>{item.region}</span>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span className="truncate max-w-[80px]">{item.tags[0]}</span>
      </div>
      <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed line-clamp-2">
        {item.synopsis}
      </p>
    </div>
  );
}